"use client"

import { Skeleton } from "@/shared/components/ui/skeleton"
import { cn } from "@/shared/lib/utils"

interface CharacterCardSkeletonProps {
  variant?: "default" | "compact"
}

/**
 * Skeleton loader that mirrors CharacterCard layout exactly:
 * same dimensions, image area, bottom overlay, name/tagline/badges/stats.
 * Use while character list is loading for instant, layout-stable feedback.
 */
export function CharacterCardSkeleton({ variant = "default" }: CharacterCardSkeletonProps) {
  const isCompact = variant === "compact"

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-border/50 bg-card",
        isCompact ? "h-[240px]" : "h-[300px] sm:h-[330px] lg:h-[350px]"
      )}
      aria-hidden
    >
      {/* Image placeholder — same area as CharacterCard image */}
      <div className="absolute inset-0">
        <Skeleton className="h-full w-full rounded-none bg-muted/80" />
        {/* Shimmer overlay for polish */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(105deg, transparent 0%, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "skeleton-shimmer 1.5s ease-in-out infinite",
          }}
        />
      </div>

      {/* Top-left badge placeholders (match New/Popular position) */}
      <div className="absolute left-3 top-3 z-10 flex gap-1.5">
        <Skeleton className="h-5 w-9 rounded-md bg-white/20" />
        <Skeleton className="h-5 w-14 rounded-md bg-white/20" />
      </div>

      {/* Bottom overlay — same gradient and padding as CharacterCard */}
      <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/60 to-transparent p-4 pt-16">
        {/* Name */}
        <Skeleton
          className={cn(
            "rounded bg-white/25",
            isCompact ? "h-3.5 w-24" : "h-5 w-28"
          )}
        />
        {/* Tagline */}
        <Skeleton
          className={cn(
            "rounded bg-white/20",
            isCompact ? "mt-1.5 h-3 w-32" : "mt-1.5 h-4 w-36"
          )}
        />

        {/* Personality badges (default only) */}
        {!isCompact && (
          <div className="mt-2 flex flex-wrap gap-1">
            <Skeleton className="h-5 w-14 rounded-full border border-white/20 bg-white/10" />
            <Skeleton className="h-5 w-16 rounded-full border border-white/20 bg-white/10" />
          </div>
        )}

        {/* Stats row — likes & messages */}
        <div className={cn("mt-2.5 flex items-center gap-3", isCompact ? "gap-2" : "")}>
          <div className="flex items-center gap-1">
            <Skeleton className={cn("rounded bg-white/20", isCompact ? "h-3 w-3" : "h-3.5 w-3.5")} />
            <Skeleton
              className={cn("rounded bg-white/20", isCompact ? "h-3 w-6" : "h-3.5 w-7")}
            />
          </div>
          <div className="flex items-center gap-1">
            <Skeleton className={cn("rounded bg-white/20", isCompact ? "h-3 w-3" : "h-3.5 w-3.5")} />
            <Skeleton
              className={cn("rounded bg-white/20", isCompact ? "h-3 w-5" : "h-3.5 w-6")}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
